import React, { ElementType, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { ValidationObj } from '../../../types/interface';
import validationUtils from '../../utils/validationUtils';
import { capitalizeFirstLetter } from 'utils/helpers';
import { t } from 'i18next';
import { UilEye, UilEyeSlash } from '@iconscout/react-unicons';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import './StandardInput.scss';

export interface StandardInputPropsI {
  id?: string;
  placeholder: string;
  label: string;
  className: string;
  icon?: React.ReactNode;
  type: React.HTMLInputTypeAttribute;
  showError: boolean;
  onChange: (value: string | number) => void;
  maxLength?: number;
  validations: ValidationObj[];
  compareValue?: string | number;
  value: string | number;
  disabled?: boolean;
  as?: ElementType;
  inputClassName?: string;
  doNotCapitalize?: boolean;
  rows?: number;
}

const StandardInput = (props: StandardInputPropsI) => {
  const [dirty, setDirty] = React.useState(false);
  const [errorKey, setErrorKey] = React.useState<string>('');
  const [errorText, setErrorText] = React.useState<string>('');
  const [valid, setValid] = React.useState(true);
  const [showPassword, setShowPassword] = React.useState(false);

  useEffect(() => {
    checkValidity(props.value);
  }, [props.compareValue, props.validations, dirty, errorText, props.value]);

  const onBlur = () => {
    setDirty(true);
  };

  const renderError = () => {
    if (!valid && (dirty || props.showError)) {
      return (
        <Form.Text className="text-danger error standard-error-text">
          {errorKey
            ? capitalizeFirstLetter(t(errorKey))
            : capitalizeFirstLetter(errorText)}
        </Form.Text>
      );
    }
    return null;
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    if (props.type === 'number') {
      newValue = newValue.replace(/[^0-9]/g, '');
    }
    if (props.maxLength && e.target.value.length > props.maxLength) {
      return;
    }
    checkValidity(newValue);
    props.onChange(newValue.trimStart());
  };

  const checkValidity = (value: string | number) => {
    let isValid = true;
    let errorText = '';
    let errorKey: string | undefined = '';
    for (const validation of props.validations || []) {
      if (validation.type == 'CompareValue') {
        if (
          !validation.disabled &&
          !validationUtils[validation.type](value, props.compareValue)
        ) {
          errorText = validation.message;
          isValid = false;
          break;
        }
      } else if (validation.type !== 'checkFormValidity') {
        if (
          !validation.disabled &&
          !validationUtils[validation.type](value, validation)
        ) {
          errorText = validation.message;
          errorKey = validation.key;
          isValid = false;
          break;
        }
      }
    }
    setValid(isValid);
    setErrorText(errorText);
    setErrorKey(errorKey);
  };

  const generateClassname = () => {
    return `${props.inputClassName} `;
  };

  const renderEyeIcon = () => {
    return (
      <ConditionalRender
        condition={showPassword}
        fallBack={
          <UilEyeSlash
            className=" cursor-pointer position-absolute  eye-icon"
            onClick={() => setShowPassword(!showPassword)}
          />
        }
      >
        <UilEye
          className=" cursor-pointer position-absolute eye-icon "
          onClick={() => setShowPassword(!showPassword)}
        />
      </ConditionalRender>
    );
  };

  return (
    <Form.Group
      className={`mb-3 text-start ${props.className} `}
      id={`standard-input`}
    >
      <div className="capitalize fs-14">
        {props.doNotCapitalize
          ? props.label
          : capitalizeFirstLetter(props.label)}
      </div>
      <div className="form-icon-container">
        <Form.Control
          id={props.id}
          type={showPassword ? 'text' : props.type}
          className={generateClassname()}
          placeholder={capitalizeFirstLetter(props.placeholder)}
          value={props.value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={props.disabled}
          readOnly={props.disabled}
          as={props.as}
          step={'.01'}
          rows={props.as === 'textarea' ? props.rows : undefined}
          key={props.id}
        />
        {props.type === 'password' ? renderEyeIcon() : null}
        {renderError()}
        {props.icon ? props.icon : null}
      </div>
    </Form.Group>
  );
};

export default StandardInput;
